import * as THREE from 'three'
import { CONFIG } from './config'

// Trapezoid outline in a unit box (x, y in -0.5…0.5), counter-clockwise.
//   top / bottom: edge widths as a fraction of the box width
//   skew:         shifts the top edge sideways (a leaning trapezoid)
export const trapezoid = ({ top = 0.78, bottom = 1, skew = 0 } = {}) => [
  [-bottom / 2, -0.5],
  [bottom / 2, -0.5],
  [top / 2 + skew, 0.5],
  [-top / 2 + skew, 0.5],
]

// Default thread attach points: the two highest corners, nudged toward the middle
// so the knots sit inside the paper even on pointy or concave shapes.
export function defaultThreads(points) {
  const cx = points.reduce((s, p) => s + p[0], 0) / points.length
  const cy = points.reduce((s, p) => s + p[1], 0) / points.length
  return [...points]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([x, y]) => [x + (cx - x) * 0.14, y + (cy - y) * 0.1])
}

// ── Sketch → room ────────────────────────────────────────────────────────────
// Lets you describe a cutout by tracing it on a drawing (pixel coordinates on a
// SKETCH.w × SKETCH.h canvas, y pointing down) instead of hand-computing 3D numbers.
//   pixels   polygon outline, in sketch pixels
//   z        depth in the room (smaller = farther back)
//   threads  optional [x, y] knot positions in sketch pixels (inside the polygon)
// Perspective is compensated so that, at rest, the cutout lands where you drew it
// no matter how far back it hangs; `depthSize` (0–1) lets distant papers still read
// a little smaller.
// `unit` = sketch pixels per world unit (lower makes the whole arrangement bigger).
export const SKETCH = { w: 2000, h: 1500, unit: 215, cx: 1000, cy: 740, yOffset: 0.5, camZ: 9 }

export function fromSketch(pixels, z, { threads, depthSize = 0.5 } = {}) {
  const xs = pixels.map((p) => p[0])
  const ys = pixels.map((p) => p[1])
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const bw = maxX - minX
  const bh = maxY - minY
  const norm = ([x, y]) => [(x - minX) / bw - 0.5, 0.5 - (y - minY) / bh]

  const k = (SKETCH.camZ - z) / SKETCH.camZ
  const cx = (minX + maxX) / 2
  const cy = (minY + maxY) / 2
  const s = 1 + (k - 1) * depthSize

  return {
    pos: [((cx - SKETCH.cx) / SKETCH.unit) * k, ((SKETCH.cy - cy) / SKETCH.unit) * k + SKETCH.yOffset, z],
    size: [(bw / SKETCH.unit) * s, (bh / SKETCH.unit) * s],
    shape: { points: pixels.map(norm), threads: threads?.map(norm) },
  }
}

// Small seeded PRNG so each cutout gets the same "scissor wobble" on every load.
export function mulberry32(a) {
  return function () {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function hashString(s) {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

// A thin slab of paper: the silhouette is extruded a few millimetres so the edge
// catches candlelight. UVs are mapped across the *nominal* bounding box, so a
// rectangular PNG is cropped by the trapezoid instead of being squashed into it.
//   group 0 → front/back faces (textured), group 1 → edges (plain paper)
export function buildCutoutGeometry(
  points,
  [w, h],
  seed,
  { thickness = 0.03, jitter = 0.01, texAspect = 0 } = {},
) {
  const rnd = mulberry32(seed)
  const shape = new THREE.Shape()
  points.forEach(([x, y], i) => {
    const px = (x + (rnd() - 0.5) * 2 * jitter) * w
    const py = (y + (rnd() - 0.5) * 2 * jitter) * h
    if (i === 0) shape.moveTo(px, py)
    else shape.lineTo(px, py)
  })
  shape.closePath()

  const geo = new THREE.ExtrudeGeometry(shape, { depth: thickness, bevelEnabled: false, steps: 1 })
  geo.translate(0, 0, -thickness / 2)

  // "Cover" fit: the image keeps its own proportions and is centre-cropped to the
  // shape's bounding box, so a 4:5 drawing is never squashed into a wide polygon.
  const box = w / h
  const t = texAspect || box
  const su = t > box ? box / t : 1
  const sv = t > box ? 1 : t / box

  const pos = geo.attributes.position
  const uv = geo.attributes.uv
  for (let i = 0; i < pos.count; i++) {
    uv.setXY(i, (pos.getX(i) / w) * su + 0.5, (pos.getY(i) / h) * sv + 0.5)
  }
  uv.needsUpdate = true
  return geo
}

// A procedural paper-fibre texture used as a bump map. Under raking candlelight the
// surface picks up tiny highlights and shadows like real paper. The pixels are
// generated once; grainFor() hands out cheap clones with their own tiling.
let grain
const grains = new Map()
export function grainFor(repeatX, repeatY) {
  const key = `${repeatX}x${repeatY}`
  if (!grains.has(key)) {
    const t = getPaperGrain().clone()
    t.repeat.set(repeatX, repeatY)
    t.needsUpdate = true
    grains.set(key, t)
  }
  return grains.get(key)
}

function getPaperGrain() {
  if (grain) return grain
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')
  const img = ctx.createImageData(size, size)
  for (let i = 0; i < size * size; i++) {
    const v = 128 + (Math.random() - 0.5) * 90
    img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v
    img.data[i * 4 + 3] = 255
  }
  ctx.putImageData(img, 0, 0)
  ctx.lineCap = 'round'
  for (let i = 0; i < 420; i++) {
    const x = Math.random() * size
    const y = Math.random() * size
    const a = Math.random() * Math.PI
    const l = 4 + Math.random() * 14
    ctx.strokeStyle = Math.random() > 0.5 ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.16)'
    ctx.lineWidth = 0.6 + Math.random()
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l)
    ctx.stroke()
  }
  grain = new THREE.CanvasTexture(canvas)
  grain.wrapS = grain.wrapT = THREE.RepeatWrapping
  grain.colorSpace = THREE.NoColorSpace
  grain.anisotropy = 4
  return grain
}

export const PAPER_EDGE = CONFIG.paper.edge
